import { forwardRef, type ChangeEvent, type KeyboardEvent } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from './Icon'
import s from './Input.module.css'

/**
 * A one-line text field with its label. The label shows above it unless `hideLabel` (then screen readers still read
 * it). `icon` sits at the start, e.g. search.
 */
export const Input = forwardRef<
  HTMLInputElement,
  {
    label: string
    value: string
    onChange: (value: string) => void
    placeholder?: string
    type?: 'text' | 'search' | 'email' | 'number'
    icon?: IconName
    hideLabel?: boolean
    hint?: string
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
    /** Extra ARIA wiring, for comboboxes such as the command palette. */
    aria?: { controls?: string; activedescendant?: string; expanded?: boolean; role?: 'combobox' }
    autoFocus?: boolean
    borderless?: boolean
  }
>(function Input(props, ref) {
  return (
    <label className={cx(s.field, props.borderless && s.borderless)}>
      <span className={props.hideLabel ? s.hidden : s.label}>{props.label}</span>
      <span className={s.box}>
        {props.icon && <Icon name={props.icon} />}
        <input
          ref={ref}
          className={s.input}
          type={props.type ?? 'text'}
          value={props.value}
          placeholder={props.placeholder}
          onChange={(e: ChangeEvent<HTMLInputElement>) => props.onChange(e.target.value)}
          onKeyDown={props.onKeyDown}
          role={props.aria?.role}
          aria-controls={props.aria?.controls}
          aria-activedescendant={props.aria?.activedescendant}
          aria-expanded={props.aria?.expanded}
          autoFocus={props.autoFocus}
          autoComplete="off"
          spellCheck={false}
        />
      </span>
      {props.hint && <span className={s.hint}>{props.hint}</span>}
    </label>
  )
})
